import React from "react";
import { brand, withAlpha, TILE_SHADOW } from "../theme/brand.js";
import { iconForService } from "../theme/icons.jsx";

export function ServiceCard({ service, entranceIndex, onStartService }) {
  const disabled = !!service.disabled;
  const wip = !disabled && !!service.inDevelopment;

  const bg = disabled ? "#f1f2f4" : wip ? brand.constructionYellowLight : brand.white;
  const border = disabled ? "#d8dbdf" : wip ? brand.constructionYellowBorder : brand.borderLight;
  const iconBg = disabled ? "#e3e5e8" : wip ? "#fde4a6" : brand.porcelainBlue;
  const iconColor = disabled ? "#8b9299" : wip ? brand.constructionYellowText : brand.prussianBlue;
  const titleColor = disabled ? "#6b7278" : wip ? brand.constructionYellowText : brand.prussianBlue;
  const bodyColor = disabled ? "#9aa0a6" : wip ? "#7a5a1f" : brand.textSecondary;
  /* The lift borrows the active organisation's palette, matching the org
     picker. Note `brand.accentColor`, not `brand.prussianBlue`: applyOrg sets
     prussianBlue from the org's *headingColor*, which is navy for IRCNoCo and
     Walla Walla — so using it made two of the four orgs hover navy instead of
     their own green and teal. accentColor is the org primary. */
  const orgPrimary = brand.accentColor || brand.prussianBlue;
  const hoverBorder = disabled ? "#d8dbdf" : wip ? brand.constructionYellow : orgPrimary;
  const subtleBorder = disabled ? "#e3e5e8" : wip ? "#f1dca0" : brand.porcelainBlue;

  const restShadow = "0 1px 3px rgba(8,49,77,0.06), 0 1px 2px rgba(8,49,77,0.04)";
  /* Two tinted layers give the colour depth; the small neutral layer keeps
     the card grounded on any background. */
  const hoverShadow = [
    `0 18px 32px ${withAlpha(TILE_SHADOW, 0.26)}`,
    `0 6px 12px ${withAlpha(TILE_SHADOW, 0.18)}`,
    "0 2px 4px rgba(8,49,77,0.12)",
  ].join(", ");

  return (
    <div
      className="novo-enter"
      onClick={() => { if (!wip && !disabled) onStartService(service.id); }}
      style={{
        // "backwards" holds the pre-animation state (invisible) through the
        // stagger delay, then releases the card to its inline styles after
        // the animation ends — so the hover transform still works.
        animation: entranceIndex != null ? "novoCardEnter 0.45s ease-out backwards" : undefined,
        animationDelay: entranceIndex != null ? `${80 + entranceIndex * 45}ms` : undefined,
        backgroundColor: bg,
        borderRadius: "12px",
        border: `1px solid ${border}`,
        padding: "24px",
        cursor: wip || disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        boxShadow: restShadow,
        position: "relative",
        gridColumn: service.featured ? "1 / -1" : undefined,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.borderColor = hoverBorder;
        e.currentTarget.style.boxShadow = hoverShadow;
        e.currentTarget.style.transform = "translateY(-5px) rotate(-0.5deg)";
        e.currentTarget.style.zIndex = "2";
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.borderColor = border;
        e.currentTarget.style.boxShadow = restShadow;
        e.currentTarget.style.transform = "translateY(0) rotate(0deg)";
        e.currentTarget.style.zIndex = "";
      }}
    >
      {disabled && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          backgroundColor: "rgba(107, 114, 120, 0.78)",
          color: "#fff",
          textAlign: "center",
          fontSize: "12px",
          fontWeight: "700",
          letterSpacing: "0.04em",
          padding: "6px 8px",
          pointerEvents: "none",
          zIndex: 1,
          fontFamily: brand.font,
        }}>
          Temporarily Disabled - Pending Review
        </div>
      )}
      {wip && (
        <span style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          fontSize: "10px",
          fontWeight: "700",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#fff",
          backgroundColor: brand.constructionYellow,
          padding: "3px 8px",
          borderRadius: "4px",
          fontFamily: brand.font,
        }}>
          In Development
        </span>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <div style={{
          width: "40px", height: "40px", borderRadius: "10px",
          backgroundColor: iconBg, color: iconColor,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {iconForService(service.id)}
        </div>
        <div style={{ flex: 1, paddingRight: wip ? "90px" : 0 }}>
          <h3 style={{ margin: "0 0 4px 0", fontSize: "15px", fontWeight: "600", color: titleColor, lineHeight: "1.3", fontFamily: brand.font }}>
            {service.name}
          </h3>
          <p style={{ margin: 0, fontSize: "13.5px", color: bodyColor, lineHeight: "1.5", fontFamily: brand.font }}>
            {service.description}
          </p>
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${subtleBorder}`, paddingTop: "10px", marginTop: "auto" }}>
        {service.companion ? (
          <p style={{ margin: 0, fontSize: "12px", color: disabled ? "#9aa0a6" : wip ? "#a68244" : brand.textMuted, lineHeight: "1.4", fontFamily: brand.font }}>
            <span style={{ fontWeight: "500", color: bodyColor }}>Often needed together with:</span>{" "}
            {service.companion}
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: "12px", color: disabled ? "#9aa0a6" : wip ? "#a68244" : brand.textMuted, fontStyle: "italic", fontFamily: brand.font }}>
            Can be used standalone.
          </p>
        )}
      </div>
    </div>
  );
}
